import React, { useState } from 'react';

import { createPortal } from 'react-dom';
import cookies from 'js-cookie';

import { Text } from '@sitecore-jss/sitecore-jss-react';

import { WarningCircle, InfoCircle, Warning, Cross } from '_utils/icons';
import { getPortal, Portals } from '_utils/helpers';
import { useTranslation } from 'react-i18next';

import {
  AnnouncementWrapper,
  TitleWrapper,
  Content,
  TextWrapper,
  IconWrapper,
  Trigger,
  TriggerIconWrapper
} from './StyledSiteWideAnnouncement';

type AnnouncementProps = {
  description: any;
  dismissable: any;
  id: any;
  link: any;
  title: any;
  type: any;
};

const Icons = {
  Primary: <InfoCircle />,
  Warning: <WarningCircle />,
  'High alert': <Warning />
};

const Announcement: React.FC<AnnouncementProps> = ({
  description,
  dismissable = { value: false },
  id,
  link,
  title,
  type
}) => {
  const [t] = useTranslation();
  const [hidden, setHidden] = useState(false);
  const announcementId = `announcement-${id}`;

  let titleWrapperAttrs = {
    as: 'div',
    href: null,
    target: null,
    tabIndex: null
  };

  if (link.value.href !== '') {
    titleWrapperAttrs.as = 'a';
    titleWrapperAttrs.href = link.value.href;
    titleWrapperAttrs.target = link.value.target;
    titleWrapperAttrs.tabIndex = hidden ? -1 : null;
  }

  const handleTriggerClick = () => {
    setHidden(true);

    cookies.set(`${announcementId}-dismissed`, true, { expires: 30 });
  };

  const portal = getPortal(Portals.before);

  // Stops spacing going out of whack
  if (hidden || portal === null) {
    return null;
  }

  return createPortal(
    <AnnouncementWrapper
      role="alert"
      $alertStyle={type.value || 'Primary'}
      $hasDescription={description.value !== ''}
      aria-hidden={hidden}
      id={announcementId}
      tabIndex={hidden ? -1 : null}
      hidden={hidden}
      className={hidden ? 'announcement-hidden' : ''}
    >
      <IconWrapper>{Icons[type.value || 'Primary']}</IconWrapper>
      <TextWrapper>
        <TitleWrapper {...titleWrapperAttrs}>
          <h2>
            <Text field={title} editable={false} />
          </h2>
        </TitleWrapper>

        {description.value !== '' && (
          <Content field={description} editable={false} tabIndex={hidden ? -1 : null} />
        )}
      </TextWrapper>
      {dismissable.value && (
        <Trigger
          type="button"
          aria-label={`${t('accessibility-close')} ${title.value}`}
          aria-controls={announcementId}
          aria-expanded={!hidden}
          onClick={handleTriggerClick}
          tabIndex={hidden ? -1 : null}
        >
          <TriggerIconWrapper aria-hidden="true">
            <Cross />
          </TriggerIconWrapper>
        </Trigger>
      )}
    </AnnouncementWrapper>,
    portal
  );
};

export default Announcement;
